import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ArtistService,
  type PerformanceDetailsData,
  type PerformanceDetailsPayload,
} from "@/services/artistService";
import { canonicalPerformingLanguageSlug } from "@/constants/performingLanguages";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";

export type { PerformanceDetailsData, PerformanceDetailsPayload };

interface PerformanceDetailsState {
  data: PerformanceDetailsData | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: PerformanceDetailsData | null) => void;
  fetchPerformanceDetails: () => Promise<PerformanceDetailsData | null>;
  ensureFetched: () => Promise<PerformanceDetailsData | null>;
  updatePerformanceDetails: (payload: PerformanceDetailsPayload) => Promise<void>;
  deletePerformingMember: (id: number) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<PerformanceDetailsData | null> | null = null;

const initialState: Omit<
  PerformanceDetailsState,
  | "setData"
  | "fetchPerformanceDetails"
  | "ensureFetched"
  | "updatePerformanceDetails"
  | "deletePerformingMember"
  | "setLoading"
  | "setError"
  | "reset"
> = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const usePerformanceDetailsStore = create<PerformanceDetailsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setData: (data) => set({ data, error: null }),

      fetchPerformanceDetails: async () => {
        if (inFlightFetch) return inFlightFetch;
        inFlightFetch = (async () => {
          set({ isLoading: true, error: null });
          try {
            const fetched = await ArtistService.getPerformanceDetails();
            // Keep offstage_crew from current store if GET didn't return it (backend may omit it)
            const current = get().data;
            const data: PerformanceDetailsData | null = fetched
              ? {
                  ...fetched,
                  offstage_crew:
                    fetched.offstage_crew != null
                      ? fetched.offstage_crew
                      : current?.offstage_crew ?? null,
                }
              : null;
            set({
              data,
              error: null,
              isLoading: false,
              hasFetchedInSession: true,
            });
            void useArtistDetailsStore.getState().fetchArtistDetails();
            return data;
          } catch (err) {
            set({
              error: "Failed to load performance details",
              isLoading: false,
            });
            return null;
          } finally {
            inFlightFetch = null;
          }
        })();
        return inFlightFetch;
      },

      ensureFetched: async () => {
        const { hasFetchedInSession, fetchPerformanceDetails, data } = get();
        if (hasFetchedInSession) return data;
        return fetchPerformanceDetails();
      },

      updatePerformanceDetails: async (payload) => {
        set({ saveLoading: true, error: null });
        try {
          await ArtistService.updatePerformanceDetails(payload);
          // Merge payload into store so offstage_crew and other updated fields are set immediately
          // (GET response may not include or may delay returning these)
          const current = get().data;
          if (current) {
            const merged: PerformanceDetailsData = {
              ...current,
              ...(payload.min_duration_mins !== undefined && { min_duration_mins: payload.min_duration_mins }),
              ...(payload.max_duration_mins !== undefined && { max_duration_mins: payload.max_duration_mins }),
              ...(payload.open_to_travel !== undefined && { open_to_travel: payload.open_to_travel }),
              ...(payload.offstage_crew !== undefined && { offstage_crew: payload.offstage_crew }),
              ...(payload.performing_language !== undefined && {
                performing_language: payload.performing_language.map((l_slug) => ({
                  l_slug,
                  language_name:
                    current.performing_language?.find(
                      (x) => x.l_slug.toLowerCase() === l_slug.toLowerCase(),
                    )?.language_name ?? canonicalPerformingLanguageSlug(l_slug),
                })),
              }),
              ...(payload.performing_members !== undefined && {
                performing_members: payload.performing_members.map((m, index) => ({
                  id: current.performing_members?.[index]?.id,
                  name: m.name,
                  role: m.role,
                  age: m.age,
                  gender: m.gender_slug,
                })),
              }),
            };
            set({ data: merged });
          }
          await get().fetchPerformanceDetails();
        } catch (err) {
          set({
            error: "Failed to save. Please try again.",
            saveLoading: false,
          });
          throw err;
        } finally {
          set({ saveLoading: false });
        }
      },

      deletePerformingMember: async (id) => {
        set({ saveLoading: true, error: null });
        try {
          await ArtistService.deletePerformingMember(id);
          const current = get().data;
          if (current?.performing_members) {
            const filtered = current.performing_members.filter((m) => m.id !== id);
            set({ data: { ...current, performing_members: filtered } });
          }
        } catch (err) {
          set({
            error: "Failed to delete performing member.",
            saveLoading: false,
          });
          throw err;
        } finally {
          set({ saveLoading: false });
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      reset: () => {
        inFlightFetch = null;
        set(initialState);
      },
    }),
    {
      name: "performance-details-storage",
      partialize: (state) => ({ data: state.data }),
    }
  )
);

